import { api } from '../api';

/**
 * What this SERVER can do — not what this user is entitled to do.
 *
 * Transport is chosen from this document before the first request, so a
 * routine configuration state (the conversation engine being off) never has to
 * be discovered by provoking an error. Entitlement is still enforced per route.
 */
export interface ConversationCapabilities {
  engine: boolean;
  textStreaming: boolean;
  realtimeVoice: boolean;
  video: boolean;
  pstn: boolean;
}

/**
 * A BeHeard admin function the CALLER holds, computed by the server with the
 * same predicates its admin routes enforce (Central platform admin + the
 * route's capability). Listing grants nothing: every route still gates itself.
 */
export type AdminFunctionKey = 'platform_dashboard' | 'usage_overview' | 'scenario_authoring';

export interface AdminFunction {
  key: AdminFunctionKey;
  label: string;
  description: string;
  /** The web screen for it (always a routed /admin page). */
  path: string;
  /** In-screen actions the caller may also take (e.g. 'moderate'); each is enforced by its route. */
  actions: string[];
}

export interface ServerCapabilities {
  conversation: ConversationCapabilities;
  /** Manager endorsement is switched on for this deployment (P4.1). */
  endorsements?: boolean;
  /** Team challenges are switched on for this deployment (P3.5). */
  teamChallenges?: boolean;
  /** Training module authoring (versioned drafts / publish) is switched on for this deployment. */
  trainingAuthoring?: boolean;
  /** Song learning is switched on for this deployment (P5.5; optional, OFF by default). */
  songLearning?: boolean;
  /**
   * Semantic retrieval over content sets (Private Knowledge practice, content
   * indexing). False until the server has a real embeddings provider and a
   * persistent vector store; absent on older servers (treat as unavailable).
   */
  rag?: boolean;
  /** The caller's admin functions. The one caller-specific field; empty for non-admins. */
  admin?: { functions: AdminFunction[] };
}

interface CapabilitiesEnvelope {
  success: boolean;
  data: ServerCapabilities;
}

export const capabilitiesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCapabilities: builder.query<ServerCapabilities, void>({
      query: () => '/capabilities',
      transformResponse: (response: CapabilitiesEnvelope) => response.data,
      providesTags: ['Capabilities'],
    }),
  }),
  overrideExisting: false,
});

export const { useGetCapabilitiesQuery } = capabilitiesApi;
